// Bedingungen bearbeiten (Filter des Workflows, Schritt Filter, Bedingungen am Custom-Activity-Auslöser).
// Eine Zeile je Bedingung: Feld ▾ · Vergleich ▾ · Wert. Alle Zeilen müssen zutreffen (UND).
import type { Bedingung, Vergleich } from "../api/workflows";
import { Auswahl, type Option } from "../components/Auswahl";
import { FeldEingabe } from "../components/FeldEingabe";
import { Icon } from "../components/Icon";
import { useMeta } from "../daten/meta";
import type { FeldDef } from "../werkzeuge/felder";
import { VERGLEICHE, leereBedingung } from "./hilfen";

type Typ = "status" | "text" | "zahl" | "datum" | "auswahl" | "nutzer" | "liste";

interface FeldOption {
  wert: string;
  label: string;
  typ: Typ;
  def?: FeldDef;
}

const OPS: Record<Typ, Vergleich[]> = {
  status: ["ist", "ist_nicht", "in", "nicht_in"],
  text: ["ist", "ist_nicht", "enthaelt", "enthaelt_nicht", "gefuellt", "leer"],
  zahl: ["ist", "groesser", "kleiner", "gefuellt", "leer"],
  datum: ["ist", "groesser", "kleiner", "gefuellt", "leer"],
  auswahl: ["ist", "ist_nicht", "in", "nicht_in", "gefuellt", "leer"],
  nutzer: ["ist", "ist_nicht", "gefuellt", "leer"],
  liste: ["gefuellt", "leer"],
};

function typVon(feldTyp: string): Typ {
  if (feldTyp === "choices") return "auswahl";
  if (feldTyp === "user") return "nutzer";
  if (feldTyp === "number") return "zahl";
  if (feldTyp === "date" || feldTyp === "datetime") return "datum";
  return "text";
}

/** Felder, nach denen sich filtern lässt: am Lead (Standard) oder an den Feldern eines Custom-Activity-Typs. */
export function useFeldOptionen(quelle: "lead" | { aktivitaetTyp: string | null }): FeldOption[] {
  const { meta } = useMeta();
  if (quelle !== "lead") {
    const typ = meta?.custom_activity_types.find((t) => t.id === quelle.aktivitaetTyp);
    return (typ?.fields ?? []).map((f) => ({ wert: f.id, label: f.name, typ: typVon(f.type), def: { ...f, type: f.type } }));
  }
  const fest: FeldOption[] = [
    { wert: "status_id", label: "Status", typ: "status" },
    { wert: "name", label: "Name", typ: "text" },
    { wert: "beschreibung", label: "Beschreibung", typ: "text" },
    { wert: "kontakt.email", label: "E-Mail des Kontakts", typ: "liste" },
    { wert: "kontakt.telefon", label: "Telefon des Kontakts", typ: "liste" },
    { wert: "alter_tage", label: "Alter des Leads (Tage)", typ: "zahl" },
  ];
  const eigene = (meta?.custom_fields.lead ?? []).map((f) => ({ wert: f.id, label: f.name, typ: typVon(f.type), def: f as FeldDef }));
  return [...fest, ...eigene];
}

function WertEingabe({ feld, b, onChange }: { feld: FeldOption | undefined; b: Bedingung; onChange: (wert: Bedingung["wert"]) => void }) {
  const { meta } = useMeta();
  if (!feld || b.op === "gefuellt" || b.op === "leer") return null;
  const liste = b.op === "in" || b.op === "nicht_in";
  if (feld.typ === "status") {
    const optionen: Option[] = (meta?.lead_statuses ?? []).map((s) => ({ wert: s.id, label: s.label, farbe: s.color }));
    const wert = Array.isArray(b.wert) ? b.wert : b.wert ? [String(b.wert)] : [];
    return liste ? (
      <Auswahl optionen={optionen} wert={wert} mehrfach platzhalter="Status wählen …" onWahl={(w) => onChange(w as string[])} />
    ) : (
      <Auswahl optionen={optionen} wert={wert[0] ?? null} platzhalter="Status wählen …" onWahl={(w) => onChange((w as string) ?? "")} />
    );
  }
  if (feld.typ === "auswahl") {
    const optionen: Option[] = (feld.def?.choices ?? []).map((c) => ({ wert: c, label: c }));
    const wert = Array.isArray(b.wert) ? b.wert : b.wert ? [String(b.wert)] : [];
    return liste ? (
      <Auswahl optionen={optionen} wert={wert} mehrfach platzhalter="Werte wählen …" onWahl={(w) => onChange(w as string[])} />
    ) : (
      <Auswahl optionen={optionen} wert={wert[0] ?? null} platzhalter="Wert wählen …" onWahl={(w) => onChange((w as string) ?? "")} />
    );
  }
  if (feld.typ === "nutzer") {
    const def = { ...(feld.def ?? { id: feld.wert, name: feld.label, choices: null, description: null, accepts_multiple_values: false }), type: "user", accepts_multiple_values: false };
    return <FeldEingabe def={def} wert={typeof b.wert === "string" ? b.wert : null} onChange={(w) => onChange((w as string) ?? "")} />;
  }
  if (feld.typ === "datum") {
    return <input className="eingabe eingabe--datum" type="date" value={typeof b.wert === "string" ? b.wert.slice(0, 10) : ""} onChange={(e) => onChange(e.target.value)} aria-label="Datum" />;
  }
  if (feld.typ === "zahl") {
    return <input className="eingabe eingabe--schmal" type="number" value={b.wert === null || b.wert === undefined ? "" : String(b.wert)} onChange={(e) => onChange(e.target.value)} aria-label="Zahl" />;
  }
  return <input className="eingabe" value={typeof b.wert === "string" ? b.wert : ""} placeholder="Wert" onChange={(e) => onChange(e.target.value)} aria-label="Wert" />;
}

interface Props {
  bedingungen: Bedingung[];
  onChange: (b: Bedingung[]) => void;
  quelle?: "lead" | { aktivitaetTyp: string | null };
  /** Text für die leere Liste */
  leerText?: string;
}

export function Bedingungen({ bedingungen, onChange, quelle = "lead", leerText }: Props) {
  const felder = useFeldOptionen(quelle);
  const standardFeld = felder[0];

  const aendern = (i: number, teil: Partial<Bedingung>) => onChange(bedingungen.map((b, n) => (n === i ? { ...b, ...teil } : b)));
  const feldWechseln = (i: number, wert: string) => {
    const f = felder.find((x) => x.wert === wert);
    const ops = OPS[f?.typ ?? "text"];
    const alt = bedingungen[i];
    onChange(bedingungen.map((b, n) => (n === i ? { feld: wert, op: ops.includes(alt.op) ? alt.op : ops[0], wert: ops.includes(alt.op) ? alt.wert : "" } : b)));
  };
  const opWechseln = (i: number, op: Vergleich) => {
    const b = bedingungen[i];
    const liste = op === "in" || op === "nicht_in";
    const wert = liste ? (Array.isArray(b.wert) ? b.wert : b.wert ? [String(b.wert)] : []) : Array.isArray(b.wert) ? (b.wert[0] ?? "") : b.wert;
    aendern(i, { op, wert });
  };

  return (
    <div className="wf-bedingungen">
      {bedingungen.length === 0 && leerText ? <div className="leise wf-bedingungen__leer">{leerText}</div> : null}
      {bedingungen.map((b, i) => {
        const feld = felder.find((f) => f.wert === b.feld.replace(/^custom\./, ""));
        const ops = OPS[feld?.typ ?? "text"];
        return (
          <div key={i} className="wf-bedingung">
            {i > 0 ? <span className="wf-bedingung__und">und</span> : <span className="wf-bedingung__und wf-bedingung__und--leer" />}
            <div className="wf-bedingung__feld">
              <Auswahl
                optionen={felder.map((f) => ({ wert: f.wert, label: f.label }))}
                wert={feld?.wert ?? null}
                platzhalter={b.feld || "Feld wählen …"}
                onWahl={(w) => w && feldWechseln(i, w as string)}
              />
            </div>
            <div className="wf-bedingung__op">
              <Auswahl optionen={ops.map((o) => ({ wert: o, label: VERGLEICHE[o] }))} wert={b.op} onWahl={(w) => w && opWechseln(i, w as Vergleich)} />
            </div>
            <div className="wf-bedingung__wert">
              <WertEingabe feld={feld} b={b} onChange={(wert) => aendern(i, { wert })} />
            </div>
            <button type="button" className="symbolknopf" title="Bedingung entfernen" aria-label="Bedingung entfernen" onClick={() => onChange(bedingungen.filter((_, n) => n !== i))}>
              <Icon name="schliessen" groesse={14} />
            </button>
          </div>
        );
      })}
      <button
        type="button"
        className="knopf knopf--klein knopf--leise wf-bedingungen__neu"
        disabled={!standardFeld}
        onClick={() => onChange([...bedingungen, { ...leereBedingung(), feld: standardFeld?.wert ?? "status_id" }])}
      >
        <Icon name="plus" groesse={13} />
        Bedingung
      </button>
    </div>
  );
}
